'use client'

/**
 * La scena dell'**ace**, dagli eventi di una sessione.
 *
 * Stessa regola del muro (`useBlockScene`) e della sostituzione: l'ace è un
 * evento tecnico, quindi chi lo vuole mostrare legge gli **eventi**. Primo giro
 * = segnaposto (niente scena per gli ace già avvenuti), poi solo gli eventi
 * nuovi.
 *
 * L'ace però è un punto: il tabellone lo conta **subito**, e la scena non deve
 * aspettare nulla per partire.
 */
import { useEffect, useRef, useState } from 'react'

export interface AceScene {
  /**
   * Chi ha fatto l'ace. La palla cade nel campo **avversario**: la scena si
   * monta sulla colonna dell'altra squadra (`side !== team`).
   */
  team: 'home' | 'away'
  /** id dell'evento: evita di animare due volte lo stesso ace. */
  id: number
}

interface Row {
  id: number
  type: string
  team: string | null
  payload: Record<string, unknown> | null
}

/** Quanto resta in onda: caduta + scoppio + tre pulsazioni. */
export const ACE_SCENE_MS = 2400

export function useAceScene(events: Row[], ready: boolean): AceScene | null {
  const [scene, setScene] = useState<AceScene | null>(null)
  const seen = useRef(0)
  const baselineDone = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!ready) return
    if (!baselineDone.current) {
      baselineDone.current = true
      seen.current = events[events.length - 1]?.id ?? 0
      return
    }
    for (const event of events) {
      if (event.id <= seen.current) continue
      seen.current = event.id
      if (event.type !== 'ace') continue
      const rawTeam = event.team ?? (typeof event.payload?.team === 'string' ? event.payload.team : null)
      const team = rawTeam === 'home' || rawTeam === 'away' ? rawTeam : null
      if (!team) continue
      setScene({ team, id: event.id })
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setScene(null), ACE_SCENE_MS)
    }
  }, [events, ready])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  return scene
}

export default useAceScene
